import React, { PropsWithChildren, useCallback, useContext, useMemo, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';
import {
    RestrictedComboModal,
    RestrictedComboModalRef,
    RestrictedComboProps,
} from '@/feature/betslip-pickem/components/RestrictedComboModal';

export type RestrictedCombo = {
    showRestrictedComboModal: (props: RestrictedComboProps) => void;
};

const RestrictedComboContext = React.createContext<RestrictedCombo | undefined>(undefined);

/**
 * Provides access to the Modal used to display the 2 players that are in a restricted combination
 */
export const RestrictedCombinationModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<RestrictedComboModalRef>(null);

    const showRestrictedComboModal = useCallback((props: RestrictedComboProps) => {
        modalRef.current?.show(props);
    }, []);

    const value: RestrictedCombo = useMemo(
        () => ({
            showRestrictedComboModal,
        }),
        [showRestrictedComboModal]
    );

    return (
        <CustomBottomSheetModalProvider>
            <RestrictedComboContext.Provider value={value}>
                {children}
                <RestrictedComboModal ref={modalRef} />
            </RestrictedComboContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const useRestrictedCombo = () => {
    const context = useContext(RestrictedComboContext);
    if (context === undefined) {
        throw new Error('useRestrictedCombo must be used within a RestrictedCombinationModalProvider');
    }
    return context;
};
